import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import type { AppRegistryPage } from '@plato/app-meta/ai-meta';
import { usePageAiMeta } from '@plato/app-shared/hooks/use-page-ai-meta';
import { CustomerListView } from '@plato/feature-customer-list/components/customer-list-view';
import { CustomerIcon } from '@plato/ui-legacy/assets/icons/customer-icon';
import { ListView } from '@plato/ui-legacy/components/list-views/list-view';
import { TableFooterWithCount } from '@plato/ui-legacy/components/tables/table-footer-with-count';
import { Page } from '@plato/ui-legacy/components/ui/page';
import { PageHeader } from '@plato/ui-legacy/components/ui/page-header';
import { DataTable } from '@plato/ui/components/data-table';
import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { PageTitle } from '../../components/page-title.tsx';
import { CustomerListFilters } from '../../features/customer-list/components/customers-list-filters.tsx';
import { useCustomerColumns } from '../../features/customer-list/hooks/use-customer-columns.tsx';
import { useCustomerListFilters } from '../../features/customer-list/hooks/use-customer-list-filters.tsx';
import { useCustomerListSorting } from '../../features/customer-list/hooks/use-customer-list-sorting.tsx';
import { useCustomerListSummary } from '../../features/customer-list/hooks/use-customer-list-summary.tsx';
import { resolveActiveMetric } from '../../server/utils/explorer-utils.ts';
import { api } from '../../utils/api.ts';

export function useCustomerListAiMeta(): AppRegistryPage {
    const { t } = useTranslation();
    return usePageAiMeta(
        {
            path: '/customers',
            title: t('page_title__customers', { ns: 'common' }),
            description:
                'The place to compare customers side by side and find the ones to act on. Sort or ' +
                'filter by the listed columns to see who is growing, declining or at risk by {{metric}} ' +
                'against last year, then open one for its detail page.',
        },
        useCustomerColumns(),
    );
}

function LegacyCustomersListView() {
    const { t } = useTranslation();
    const { appliedFilters } = useCustomerListFilters();
    const { billingStatus } = resolveActiveMetric(useAppConfig());
    const columns = useCustomerColumns(appliedFilters.sortingFilter.field);

    const [sorting, sortChangedInInterface] = useCustomerListSorting();
    const { summary } = useCustomerListSummary();

    const {
        data: customerListData,
        isLoading: isLoadingCustomerList,
        isFetchingNextPage: isFetchingCustomerListNextPage,
        hasNextPage,
        fetchNextPage,
        isSuccess: isDoneGettingList,
    } = api.platoUser.getManyCustomers.getListWithFilters.useInfiniteQuery(
        { ...appliedFilters, billingStatus },
        {
            getNextPageParam: (lastPage) => lastPage.nextCursor,
        },
    );

    const { data: count, isLoading: isLoadingCount } =
        api.platoUser.getManyCustomers.getCount.useQuery(appliedFilters);

    const customerList = useMemo(() => {
        if (customerListData) {
            return customerListData.pages.flatMap((page) => page.result);
        } else {
            return [];
        }
    }, [customerListData]);

    return (
        <Page>
            <PageHeader
                icon={CustomerIcon}
                subTitle={t('page_sub_title__customers')}
                title={t('page_title__customers')}
            />
            <CustomerListFilters canGetFilters={isDoneGettingList} />
            <ListView>
                <ListView.BodyContainer>
                    <DataTable
                        emptyLabel={t('empty_table__no_results')}
                        isLoading={isLoadingCustomerList}
                        isFetchingNextPage={isFetchingCustomerListNextPage}
                        onFetchNextPage={fetchNextPage}
                        hasNextPage={hasNextPage}
                        data={customerList}
                        columns={columns}
                        manualSorting
                        sorting={sorting}
                        onSortingChange={sortChangedInInterface}
                        summary={summary}
                    />
                    <TableFooterWithCount loading={isLoadingCount} count={count} />
                </ListView.BodyContainer>
            </ListView>
        </Page>
    );
}

function CustomersListPage(): ReactElement {
    const { t } = useTranslation();
    const { enableCustomerListFeatureSlice } = useAppConfig();

    return (
        <>
            <PageTitle title={t('page_title__customers')} />
            {enableCustomerListFeatureSlice ? <CustomerListView /> : <LegacyCustomersListView />}
        </>
    );
}

export default CustomersListPage;
